import React, { RefObject } from 'react';
import classnames from 'classnames';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import AppModal from '@/components/AppModal/AppModal';
import TheButton from '@/components/school-management/TheButton/TheButton';
import CsvDropzone, {
  CsvDropzoneRef,
} from '@/components/school-management/CsvDropzone/CsvDropzone';
import { UserData } from '@/school-management/types/user-data';
import SVG from 'react-inlinesvg';
import download from '@/assets/svg/download.svg';
import closeIcon from '@/assets/svg/close.svg';
import classNames from './styles.module.scss';

interface ClassUploadCsvModalProps {
  show: boolean;
  addUsersToNewGroup: boolean;
  csvFilesAdded: boolean;
  isCsvLoading: boolean;
  usersData: UserData[];
  setUsersData: React.Dispatch<React.SetStateAction<UserData[]>>;
  setIsCsvLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setCsvFilesAdded: React.Dispatch<React.SetStateAction<boolean>>;
  csvDropzoneRef: RefObject<CsvDropzoneRef>;
  onClose: () => void;
  onAddManually: () => void;
  onNext: () => void;
  isTeacher?: boolean;
  usersInClass?: number;
}

const ClassUploadCsvModal: React.FC<ClassUploadCsvModalProps> = ({
  show,
  addUsersToNewGroup,
  csvFilesAdded,
  isCsvLoading,
  usersData,
  setUsersData,
  setIsCsvLoading,
  setCsvFilesAdded,
  csvDropzoneRef,
  onClose,
  onAddManually,
  onNext,
  isTeacher,
  usersInClass = 0,
}) => {
  const { t } = useAdminTranslation();

  if (!show) return null;

  return (
    <AppModal show={show} type='fade'>
      <div className={classnames(classNames.modal, classNames.fileUploadModal)}>
        {(!csvFilesAdded || isCsvLoading) && (
          <button onClick={onClose} className={classNames.close} aria-label={t('close').toString()}>
            <SVG src={closeIcon} />
          </button>
        )}
        <h2>
          {addUsersToNewGroup
            ? isTeacher
              ? t('settings.add_teachers.invite_teachers')
              : t('settings.add_students.invite_students')
            : isTeacher
              ? t('settings.add_teachers.enroll_teachers')
              : t('settings.add_students.enroll_students')}
        </h2>
        <span className={classNames.description}>
          {isTeacher
            ? t('settings.add_teachers.upload_file')
            : t('settings.add_students.upload_file')}
        </span>
        <div className={classNames.descriptionWrapper}>
          <span className={classNames.sample}>
            <a href='/csv/sample.xlsx' target='_blank' className={classNames.downloadLink}>
              <SVG src={download} />
              {isTeacher
                ? t('settings.add_teachers.download')
                : t('settings.add_students.download')}
            </a>
            <span>
              {isTeacher
                ? t('settings.add_teachers.download_sample')
                : t('settings.add_students.download_sample')}
            </span>
          </span>
        </div>
        <CsvDropzone
          ref={csvDropzoneRef}
          usersData={usersData}
          setUsersData={setUsersData}
          isCsvLoading={isCsvLoading}
          setIsCsvLoading={setIsCsvLoading}
          setCsvFilesAdded={setCsvFilesAdded}
          usersInClass={usersInClass}
          type={isTeacher ? 'teachers' : 'students'}
        />
        <div className={`${classNames.buttons} ${classNames.buttonsUpload}`}>
          {(!csvFilesAdded || isCsvLoading) && (
            <TheButton
              className={classNames.addManuallyBtn}
              text={
                isTeacher
                  ? t('settings.add_teachers.add_teachers_manually')
                  : t('settings.add_students.add_students_manually')
              }
              plain
              emitOnClick={onAddManually}
            />
          )}
          {csvFilesAdded && !isCsvLoading && (
            <>
              <TheButton
                className={classNames.cancelBtn}
                text={t('cancel')}
                plain
                emitOnClick={() => {
                  if (csvDropzoneRef.current) {
                    csvDropzoneRef.current.removeFile();
                  }
                }}
              />
              <TheButton
                className={classNames.sendBtn}
                text={
                  isTeacher
                    ? t('settings.add_teachers.button_next')
                    : t('settings.add_students.button_next')
                }
                type='submit'
                disabled={!usersData.length || isCsvLoading}
                shouldRecolor={false}
                emitOnClick={onNext}
              />
            </>
          )}
        </div>
      </div>
    </AppModal>
  );
};

export default ClassUploadCsvModal;
